import { DatePicker } from '@syncfusion/ej2-calendars';
import { DataManager, UrlAdaptor } from '@syncfusion/ej2-data';
import { ComboBox, DropDownList } from '@syncfusion/ej2-dropdowns';
import { Grid } from '@syncfusion/ej2-grids';
import { TextBox } from '@syncfusion/ej2-inputs';
import { Dialog } from '@syncfusion/ej2-popups';

import { BulkUpdateFieldConfig, getBulkUpdateFields } from '@/shared/bulkUpdate/fields';
import { BulkUpdateRequestBody, BulkUpdateResponseBody } from '@/shared/bulkUpdate/types';
import { routes } from '@/shared/routes';
import { ModelName } from '@/shared/types/model';
import { DEFAULT_LOCALE, dateInputFormat } from '@/shared/utils/format';

import {
  buildBulkUpdateSelection,
  getBulkUpdateModelName,
  getSelectedCount,
  registerBulkUpdateOpenHandler,
  resetSelectionState,
  updateBulkUpdateBar,
} from '@/front/bulkUpdateSelection';
import { POST } from '@/front/fetch';
import { alertModal, confirmModal } from '@/front/modal';

type FieldControlInstance = DropDownList | ComboBox | TextBox | DatePicker;

type FieldControl = {
  config: BulkUpdateFieldConfig;
  checkbox: HTMLInputElement;
  control?: FieldControlInstance;
};

let dialog: Dialog | null = null;
let fieldControls: FieldControl[] = [];
let currentGridId: string | null = null;
let currentGrid: Grid | null = null;
let currentModel: ModelName | null = null;
let pendingFields: BulkUpdateFieldConfig[] = [];
let isSubmitting = false;

const fieldInputId = (field: string) => `bulk-update-field-${field}`;
const fieldCheckboxId = (field: string) => `bulk-update-enable-${field}`;

const buildContent = (fields: BulkUpdateFieldConfig[]) => `
  <div class="d-flex flex-column gap-3 bulk-update-modal-body">
    ${fields
      .map(
        (field) => `
      <div class="d-flex align-items-start gap-3">
        <div class="form-check mb-0 pt-1" style="min-width: 180px">
          <input class="form-check-input" type="checkbox" id="${fieldCheckboxId(field.field)}" data-bulk-field="${field.field}" />
          <label class="form-check-label" for="${fieldCheckboxId(field.field)}">${field.label}</label>
        </div>
        ${
          field.fixedValue !== undefined
            ? ''
            : `<div class="flex-grow-1">
          ${
            field.control === 'text'
              ? `<textarea id="${fieldInputId(field.field)}"></textarea>`
              : `<input id="${fieldInputId(field.field)}" />`
          }
        </div>`
        }
      </div>
    `,
      )
      .join('')}
  </div>
`;

const destroyControls = () => {
  for (const { control } of fieldControls) {
    control?.destroy();
  }
  fieldControls = [];
};

const setControlEnabled = (control: FieldControlInstance, enabled: boolean) => {
  control.enabled = enabled;
  if (!enabled) {
    if (control instanceof TextBox) {
      control.value = '';
    } else {
      control.value = null as any;
    }
  }
};

const renderPlaceholder = (action: string, config: BulkUpdateFieldConfig) => {
  let modelName = config.label;

  if (config.control === 'date') {
    modelName = 'Date';
  } else if (config.control === 'lookup') {
    modelName = config.lookupModel ?? 'Record';

    if (modelName === 'MemberView') {
      modelName = 'User';
    }
  }

  return `${action} ${modelName}${config.clearable ? ` (leave blank to clear)` : ''}`;
};

const createControl = (config: BulkUpdateFieldConfig): FieldControlInstance => {
  if (config.control === 'text') {
    const textBox = new TextBox({
      floatLabelType: 'Never',
      multiline: true,
      enabled: false,
      width: '100%',
      placeholder: renderPlaceholder('Enter', config),
    });
    textBox.appendTo(`#${fieldInputId(config.field)}`);
    return textBox;
  }

  if (config.control === 'date') {
    const datePicker = new DatePicker({
      locale: DEFAULT_LOCALE.code,
      placeholder: renderPlaceholder('Select', config),
      format: dateInputFormat,
      showClearButton: config.clearable,
      enabled: false,
      width: '100%',
    });
    datePicker.appendTo(`#${fieldInputId(config.field)}`);
    return datePicker;
  }

  if (config.control === 'lookup' && config.lookupModel) {
    const combo = new ComboBox({
      dataSource: new DataManager({
        url: routes.data.lookup.list({ model: config.lookupModel }),
        adaptor: new UrlAdaptor(),
      }),
      fields: { value: 'value', text: 'text' },
      placeholder: renderPlaceholder('Select', config),
      allowFiltering: true,
      allowCustom: false,
      showClearButton: config.clearable,
      enabled: false,
      width: '100%',
    });
    combo.appendTo(`#${fieldInputId(config.field)}`);
    return combo;
  }

  const dropdown = new DropDownList({
    dataSource: config.options ?? [],
    fields: { value: 'value', text: 'text' },
    placeholder: renderPlaceholder('Select', config),
    allowFiltering: true,
    showClearButton: config.clearable,
    enabled: false,
    width: '100%',
    popupHeight: '220px',
  });
  dropdown.appendTo(`#${fieldInputId(config.field)}`);
  return dropdown;
};

const initControls = (fields: BulkUpdateFieldConfig[]) => {
  destroyControls();

  for (const config of fields) {
    const checkbox = document.getElementById(fieldCheckboxId(config.field)) as HTMLInputElement | null;
    if (!checkbox) continue;

    checkbox.checked = false;

    if (config.fixedValue !== undefined) {
      fieldControls.push({ config, checkbox });
      continue;
    }

    const control = createControl(config);

    checkbox.addEventListener('change', () => {
      setControlEnabled(control, checkbox.checked);
    });

    fieldControls.push({ config, checkbox, control });
  }
};

const getControlValue = (control: FieldControlInstance): string | number | null => {
  if (control instanceof TextBox) {
    const value = control.value?.trim() ?? '';
    return value === '' ? null : value;
  }

  if (control instanceof DatePicker) {
    const value = control.value;
    if (!value) return null;
    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) return null;
    date.setHours(12, 0, 0, 0);
    return date.toISOString();
  }

  const raw = control.value;
  return raw === undefined || raw === null || raw === '' ? null : (raw as string | number);
};

const collectUpdates = (): BulkUpdateRequestBody['updates'] | null => {
  const updates: BulkUpdateRequestBody['updates'] = {};
  let hasEnabled = false;

  for (const { config, checkbox, control } of fieldControls) {
    if (!checkbox.checked) continue;
    hasEnabled = true;

    if (config.fixedValue !== undefined) {
      updates[config.field] = { value: config.fixedValue };
      continue;
    }

    if (!control) {
      alertModal(`Missing control for ${config.label}.`);
      return null;
    }

    const value = getControlValue(control);

    if (value === null && !config.clearable) {
      alertModal(`Please select a value for ${config.label}, or untick the field.`);
      return null;
    }

    updates[config.field] = { value };
  }

  if (!hasEnabled) {
    alertModal('Please enable at least one field to update.');
    return null;
  }

  return updates;
};

const closeDialog = () => {
  dialog?.hide();
};

const setDialogButtonsDisabled = (disabled: boolean) => {
  document.querySelectorAll<HTMLButtonElement>('#bulkUpdateDialog .e-footer-content button').forEach((button) => {
    button.disabled = disabled;
  });
};

const submitBulkUpdate = async () => {
  if (isSubmitting || !currentGridId || !currentGrid || !currentModel) return;

  const gridId = currentGridId;
  const grid = currentGrid;
  const model = currentModel;

  const updates = collectUpdates();
  if (!updates) return;

  const selection = buildBulkUpdateSelection(gridId, grid);
  if (!selection) {
    alertModal('No records selected.');
    return;
  }

  const count = getSelectedCount(gridId, grid);
  const confirmed = await confirmModal(
    `Are you sure you want to update ${count} ${count === 1 ? 'record' : 'records'}?`,
    'Apply',
    'primary',
  );

  if (!confirmed) return;

  isSubmitting = true;
  setDialogButtonsDisabled(true);

  try {
    const body: BulkUpdateRequestBody = { selection, updates };
    const res = await POST(routes.data.bulkUpdate({ model }), body);

    if (!res.ok) {
      const errorBody = await res.json().catch(() => null);
      const message = errorBody?.message ?? 'Bulk update failed.';
      alertModal(message);
      return;
    }

    const result = (await res.json()) as BulkUpdateResponseBody;

    isSubmitting = false;
    if (currentGridId === gridId && currentGrid === grid) {
      closeDialog();
    }
    resetSelectionState(gridId);
    grid.clearSelection();
    updateBulkUpdateBar(gridId, grid);
    grid.refresh();

    alertModal(`Updated ${result.count} ${result.count === 1 ? 'record' : 'records'}.`);
  } catch (error) {
    console.error('Failed to bulk update records', error);
    alertModal('Bulk update failed. Please try again.');
  } finally {
    isSubmitting = false;
    setDialogButtonsDisabled(false);
  }
};

const ensureDialog = () => {
  if (dialog) return dialog;

  const host = document.createElement('div');
  host.id = 'bulkUpdateDialog';
  document.body.appendChild(host);

  dialog = new Dialog({
    header: 'Bulk Update',
    content: '',
    target: document.body,
    showCloseIcon: true,
    isModal: true,
    width: '560px',
    zIndex: 1035,
    visible: false,
    closeOnEscape: true,
    animationSettings: { effect: 'Fade' },
    beforeClose: (args) => {
      if (isSubmitting) {
        args.cancel = true;
      }
    },
    buttons: [
      {
        click: () => closeDialog(),
        buttonModel: { content: 'Cancel', cssClass: 'e-btn e-flat' },
      },
      {
        click: () => void submitBulkUpdate(),
        buttonModel: { content: 'Apply', cssClass: 'e-btn e-primary' },
      },
    ],
    open: () => {
      isSubmitting = false;
      setDialogButtonsDisabled(false);
      if (pendingFields.length) {
        initControls(pendingFields);
      }
    },
    close: () => {
      destroyControls();
      pendingFields = [];
      currentGridId = null;
      currentGrid = null;
      currentModel = null;
    },
  });

  dialog.appendTo(host);
  return dialog;
};

export const openBulkUpdateModal = (gridId: string, grid: Grid) => {
  const model = getBulkUpdateModelName(grid);
  if (!model) {
    alertModal('Bulk update is not available for this view.');
    return;
  }

  const selection = buildBulkUpdateSelection(gridId, grid);
  if (!selection) {
    alertModal('Please select at least one record.');
    return;
  }

  const archived = grid.element.dataset.archived === 'true';
  const fields = getBulkUpdateFields(model, { archived });
  if (!fields.length) {
    alertModal('No bulk update fields configured.');
    return;
  }

  currentGridId = gridId;
  currentGrid = grid;
  currentModel = model;
  pendingFields = fields;

  const dlg = ensureDialog();
  dlg.content = buildContent(fields);
  dlg.dataBind();
  dlg.show();
};

registerBulkUpdateOpenHandler(openBulkUpdateModal);
